import { Injectable } from '@angular/core';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { Participant } from '../../_shared/models/participant';
import { User } from '../../_shared/models/user.model';
import { Preferences } from '@capacitor/preferences';
import { addSeconds } from 'date-fns';
import { AuthService } from '../../auth/auth.service';
import { TournamentDetailsService } from '../../tournaments/tournament-details/tournament-details.service';
import { TieDetailsService } from '../../ties/tie-details/tie-details.service';
import { TrackerBarService } from '../../scoreboard/tracker-bar/tracker-bar.service';
import { ToastService } from './toast.service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LoaderService } from './loader.service';

const phoneNumberUtil = PhoneNumberUtil.getInstance();

@Injectable({ providedIn: 'root' })

export class HelperService {


    constructor(
        private authService: AuthService,
        private tieDetailsService: TieDetailsService,
        private trackerBarService: TrackerBarService,
        private tournamentDetailsService: TournamentDetailsService,
        private toast: ToastService,
        private router: Router,
        private loader: LoaderService
    ) { }


    /**
     * Get country code
     */
    getCountryCode(phoneNumber) {
        return phoneNumberUtil.parse(phoneNumber).getCountryCode();
    }

    /**
    * Get national numbers
    */
    getNationalNumber(phoneNumber) {
        return phoneNumberUtil.parse(phoneNumber).getNationalNumber();
    }


    getParticipantName(participant: Participant, type = 'initial') {
        let participantName = '';

        const players = participant.participantType === 'PAIR' ? participant.members : [participant.person];
        players.forEach((person: User, i) => {
            if (i === 1) {
                participantName += '/';
            }

            const firstName = type === 'initial' ? person.first_name.charAt(0) + '.' : person.first_name;
            if (type === 'initial') {
                participantName += `${person.last_name} ${firstName}`;
            } else {
                participantName += `${firstName} ${person.last_name}`;
            }
        });


        return participantName;

    }

    async canUserMakeCall(key: string, freezTimeInSeconds: number): Promise<boolean> {
        const lastCallMade = await Preferences.get({ key });
        return lastCallMade?.value
            ? new Date() > addSeconds(new Date(lastCallMade.value), freezTimeInSeconds)
            : true;
    }

    async setLastCallMadeTime(key: string) {
        await Preferences.set({
            key,
            value: new Date().toISOString()
        })
    }

    async removeFromStorage(key: string) {
        await Preferences.remove({
            key
        })
    }

    async addScorekeeperWithContext(contextId: string, contextType: string, matchTournamentId: string, matchStatus: string): Promise<Subscription | null> {
        try {            
            this.loader.present();
            const authUser: any = await this.authService.getAuthUser();
            
            if (!authUser) {
                this.toast.presentDangerToast('Authentication required. Please log in again.');
                return null;
            }

            let subscription: Subscription;

            switch (contextType) {
                case 'tie':
                    subscription = this.tieDetailsService.addTracker(contextId, [], true, authUser.phone, authUser.email).subscribe({
                        next: () => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentToast('You have been added as a scorekeeper');
                            this.loader.dismiss();
                        },
                        error: (err) => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentDangerToast(err);
                            this.loader.dismiss();
                        }
                    });
                    break;
                case 'match':
                    subscription = this.trackerBarService.addTracker(contextId, [], true, authUser.phone, authUser.email).subscribe({
                        next: () => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentToast('You have been added as a scorekeeper');
                            this.loader.dismiss();
                        },
                        error: (err) => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentDangerToast(err);
                            this.loader.dismiss();
                        }
                    });
                    break;
                case 'tournament':
                    subscription = this.tournamentDetailsService.addTracker(contextId, [], true, authUser.phone, authUser.email).subscribe({
                        next: () => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentToast('You have been added as a scorekeeper');
                            this.loader.dismiss();
                        },
                        error: (err) => {
                            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
                            this.toast.presentDangerToast(err);
                            this.loader.dismiss();
                        }
                    });
                    break;
                default:
                    this.toast.presentDangerToast('Invalid context type. Please try again.');
                    this.loader.dismiss();
                    return null;
            }

            return subscription;
        } catch (error) {
            this.removeDataFromStorageAndNavigate(contextId, contextType, matchTournamentId, matchStatus);
            this.toast.presentDangerToast('An unexpected error occurred. Please try again.');
            this.loader.dismiss();
            return null;
        }
    }

    removeDataFromStorageAndNavigate(contextId: string, contextType: string, matchTournamentId: string, matchStatus: string) {
        localStorage.removeItem('ScorekeeperContextType');
        localStorage.removeItem('ScorekeeperContextId');
        localStorage.removeItem('ScorekeeperMatchTournamentId');
        localStorage.removeItem('ScorekeeperMatchStatus');
        
        if (contextType === 'tie') {
            this.router.navigateByUrl('/ties/' + contextId);
        } else if (contextType === 'match' && matchTournamentId) {
            const tournamentPage = matchStatus.includes('LIVE_SCORE') ? 'live-score' : 'upcoming';
            this.router.navigateByUrl('/tournaments/' + matchTournamentId + '/' + tournamentPage);
        } else if(contextType === 'tournament') {
            this.router.navigateByUrl('/tournaments/' + contextId + '/live-score');
        } else {
            this.router.navigateByUrl('/individual');
        }
    }

    setScorekeeperContext(contextId: string, contextType: string, matchTournamentId: string, matchStatus: string) {
        localStorage.setItem('ScorekeeperContextType', contextType);
        localStorage.setItem('ScorekeeperContextId', contextId);
        localStorage.setItem('ScorekeeperMatchTournamentId', matchTournamentId);
        localStorage.setItem('ScorekeeperMatchStatus', matchStatus);
    }

    getScorekeeperContext() {
        return {
            contextType: localStorage.getItem('ScorekeeperContextType'),
            contextId: localStorage.getItem('ScorekeeperContextId'),
            matchTournamentId: localStorage.getItem('ScorekeeperMatchTournamentId'),
            matchStatus: localStorage.getItem('ScorekeeperMatchStatus')
        };
    }
}

